import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import FilterBar, { defaultFilters, type MovieFilters } from './components/FilterBar';

const PAGE_SIZE = 8;

const sortOptions: { value: MovieFilters['sort']; label: string }[] = [
  { value: 'default', label: '按相关度' },
  { value: 'rating', label: '按评分' },
  { value: 'year', label: '按上映时间' },
];

export default function Movies() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialGenre = searchParams.get('genre');
  const keyword = searchParams.get('q')?.trim() ?? '';

  const [filters, setFilters] = useState<MovieFilters>({
    ...defaultFilters,
    genres: initialGenre ? [initialGenre] : [],
  });
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [filters, keyword]);

  const filtered = useMemo(() => {
    let list = movies.filter((m) => {
      const genreOk = filters.genres.length === 0 || m.genres.some((g) => filters.genres.includes(g));
      const yearOk = m.year >= filters.yearMin && m.year <= filters.yearMax;
      const ratingOk = m.rating >= filters.ratingMin;
      const kwOk =
        !keyword ||
        m.title.includes(keyword) ||
        m.enTitle.toLowerCase().includes(keyword.toLowerCase()) ||
        m.genres.some((g) => g.includes(keyword));
      return genreOk && yearOk && ratingOk && kwOk;
    });

    if (filters.sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    else if (filters.sort === 'year') list = [...list].sort((a, b) => b.year - a.year);
    else list = [...list].sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)));

    return list;
  }, [filters, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setFilters({ ...defaultFilters });
    setSearchParams({});
  };

  return (
    <div className="w-full">
      <section className="bg-primary-950 text-background-50">
        <div className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
          <Breadcrumb
            className="mb-5 [&_a]:text-background-200/80 [&_a:hover]:text-accent-400 [&_span]:text-background-50"
            items={[{ label: '首页', to: '/' }, { label: '电影列表' }]}
          />
          <h1 className="text-2xl md:text-4xl font-bold">电影分类</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-background-200/85 leading-relaxed">
            按类型、上映年份与评分快速筛选你想看的电影。列表中的每一部作品都配有剧情介绍、专业影评与相关推荐，
            点击卡片即可进入详情页深入了解。
          </p>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          <aside className="lg:col-span-1">
            <FilterBar
              filters={filters}
              onChange={setFilters}
              onReset={reset}
              mobileOpen={mobileOpen}
              onOpenMobile={() => setMobileOpen(true)}
              onCloseMobile={() => setMobileOpen(false)}
            />
          </aside>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <p className="text-sm text-foreground-500">
                {keyword ? (
                  <>
                    搜索「<span className="text-foreground-900 font-medium">{keyword}</span>」共找到{' '}
                    <span className="text-primary-600 font-semibold">{filtered.length}</span> 部电影
                  </>
                ) : (
                  <>
                    共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 部电影
                  </>
                )}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
                <select
                  value={filters.sort}
                  onChange={(e) =>
                    setFilters({ ...filters, sort: e.target.value as MovieFilters['sort'] })
                  }
                  className="h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
                >
                  {sortOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                  {pageItems.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
                <div className="mt-8 md:mt-10">
                  <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
                </div>
              </>
            ) : (
              <div className="py-16 md:py-24 text-center rounded-lg border border-dashed border-background-300 bg-background-50">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <p className="mt-4 text-base font-medium text-foreground-800">未找到符合条件的电影</p>
                <p className="mt-1 text-sm text-foreground-500">试试放宽筛选条件，或查看全部电影。</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}